import { FormPanelLoad, formPanelStatusOf } from "../components/FormPanel";
import { SelectOptionTypes } from "../components/Input";
import { Accounts, useGetAccountsQuery } from "../services";
import { AccountType } from "../utils/types";

interface AccountOptionsFilter {
  excludeId?: number;
  investmentsOnly?: boolean;
  accountType?: AccountType;
}

const ACCOUNTS_ERROR_MESSAGE = "Failed to load accounts. Please try again.";

// Every picker here moves cash, and a broker settled through a linked account holds none.
const holdsCash = (account: Accounts) =>
  account.cashSettlement !== "LINKED_ACCOUNT";

export const useAccountOptions = ({
  excludeId,
  investmentsOnly = false,
  accountType,
}: AccountOptionsFilter) => {
  const { data, isFetching, isError, refetch } = useGetAccountsQuery();

  const accountOptions: SelectOptionTypes[] = (data ?? [])
    .filter(
      (account) =>
        account.id !== excludeId &&
        holdsCash(account) &&
        (!investmentsOnly || account.investmentId !== null) &&
        (accountType === undefined || account.accountType === accountType),
    )
    .map((account) => ({
      value: String(account.id),
      label: account.name,
    }));

  // A failed refetch keeps the accounts already loaded, so only a load with nothing to show blocks the form.
  const hasNoAccounts = data === undefined;
  const load: FormPanelLoad = {
    status: formPanelStatusOf({
      isLoading: isFetching && hasNoAccounts,
      isError: isError && hasNoAccounts,
    }),
    onRetry: refetch,
    loadingLabel: "Loading accounts…",
    errorMessage: ACCOUNTS_ERROR_MESSAGE,
  };

  return { accountOptions, accounts: data, load };
};
